/**
 * 딜 상세 — 서버 구성 V2 그룹 렌더 (2026-07-30).
 *
 * 서버는 `deal_item` 인접 리스트로 저장된다.
 *   line_type='server', parent_id=NULL      → 서버 헤더행 (대수·번들가·컨디션)
 *   line_type='part',   parent_id=서버행 id → 서버 내부 부품
 *
 * 화면 계약:
 *   - 헤더행: 품목칸 'Server', 품명칸은 **약식 요약(여러 줄)**, 컨디션은 '신품/중고 서버',
 *     수량=서버 대수, 판매 단가=번들가, 작업=⋯(수정/복제/삭제).
 *     대수와 번들가는 **여기서 직접 고친다**(`CONFIG2-018`) — 대수는 애초에
 *     Config 에 없는 딜 전용 값이고, 번들가도 딜에서 바로 조정할 수 있어야 한다.
 *   - 기본 접힘. chevron 또는 요약 전체를 눌러 펼친다(같은 control).
 *   - 자식행: 품목칸=카테고리, 컨디션 **빈칸**(V2 구성품은 독립 컨디션 없음),
 *     수량은 읽기전용 `nEA`, 소스·판매단가만 편집 가능, 작업칸은 빈칸.
 *   - 구성 변경은 별탭 편집기(`수정`)로만 한다 — 여기서 부품을 추가/삭제하지 않는다.
 *   - 확정(locked)되면 전부 읽기 전용 텍스트로 되돌아간다.
 *
 * 약식 요약은 shared 생성기를 그대로 쓴다(Config·Deal·Quote 공용).
 */

import { escapeHtml } from '../../lib/cells';
import {
  sourceSelectHtml, selectedSourceValue, type SourcePool,
} from '../../lib/source-options';
import * as api from '../../api/client';
import { withServerSuffix } from '../../lib/server-desc';
import {
  buildServerDescription, type ShorthandItem,
} from '@hcltech/shared/server-config/shorthand';
import {
  buildServerConfigName, firstDescriptionLine,
} from '@hcltech/shared/server-config/config-name';
import type { SpecCategory } from '@hcltech/shared/server-config/spec-category';
import type { DealDetailData } from './types';
import { shorthandFixWarningHtml, shorthandItemLabel } from './types';

type Item = NonNullable<api.DealFull['items']>[number];

const won = (n: number | null | undefined): string =>
  n == null ? '' : Number(n).toLocaleString('ko-KR');

/** 서버 헤더행 + 그 자식들을 묶는다. */
export type ServerGroup = { server: Item; children: Item[] };

/** 활성 품목 목록에서 서버 그룹만 추출 (sort_order 순). */
export function collectServerGroups(items: readonly Item[]): ServerGroup[] {
  const servers = items
    .filter((it) => it.line_type === 'server' && it.is_active)
    .sort((a, b) => a.sort_order - b.sort_order);
  return servers.map((server) => ({
    server,
    children: items
      .filter((it) => it.parent_id != null && String(it.parent_id) === String(server.id) && it.is_active)
      .sort((a, b) => a.sort_order - b.sort_order),
  }));
}

/**
 * 접힌 약식 요약 문자열 — **폴백 전용**.
 *
 * 정본은 헤더행의 `item_description` 이다(백엔드가 생성/수정 때마다 같은 shared
 * 생성기로 만들어 저장한다). 이 함수는 그 컬럼이 비어 있던 이전 데이터에서만 쓴다.
 *
 * 자식행이 이미 카탈로그 스펙을 갖고 있지 않으므로(딜에는 스냅샷된 디스크립션만 있다),
 * 저장된 item_description 을 FREE_TEXT 로 넘겨 생성기가 재작성하지 않게 한다.
 * → 카테고리별 줄 배치·DISK 병합·RAID+Battery·NIC 넘버링·Railkit 규칙만 적용된다.
 */
export function serverSummary(children: readonly Item[]): string {
  const items: ShorthandItem[] = children
    .filter((c) => !!c.spec_category)
    .map((c) => ({
      category:            c.spec_category as SpecCategory,
      quantity:            c.quantity ?? 1,
      descriptionMode:     'FREE_TEXT',
      descriptionOverride: c.item_description ?? c.description_raw ?? '',
      descriptionRaw:      c.description_raw ?? null,
    }));
  return buildServerDescription(items);
}

// ─── `+ 서버 추가` 모달 ──────────────────────────────────────────────────────
/**
 * 두 갈래를 먼저 고르게 한다(프리뷰 정합).
 *   신규 구성 만들기   — 별탭 편집기에서 Chassis 부터
 *   기존 구성 불러오기 — **다른 딜**의 서버를 이 구성으로 복사
 *
 * 같은 딜 안의 복제는 서버 행 ⋯ 메뉴에 이미 있으므로 목록에서 현재 딜은 뺀다.
 * 불러오기는 구성만 가져오고 가격·소스는 승계하지 않는다 — 안내 문구로 명시한다.
 */
export function renderServerAddModal(d: DealDetailData): string {
  const m = d.serverAdd;
  if (!m.open) return '';

  const body = m.mode === 'pick'
    ? `
      <p class="deal-svradd-lead">
        새 서버를 구성하거나 다른 Deal의 서버 구성을 복사합니다.
      </p>
      <div class="deal-svradd-choices">
        <button class="deal-svradd-choice" type="button" data-action="server-add-new">
          <span class="deal-svradd-choice-title">신규 구성 만들기</span>
          <span class="deal-svradd-choice-desc">새 탭에서 Chassis부터 선택해 서버를 구성합니다.</span>
        </button>
        <button class="deal-svradd-choice" type="button" data-action="server-add-import">
          <span class="deal-svradd-choice-title">기존 구성 불러오기</span>
          <span class="deal-svradd-choice-desc">다른 Deal에 저장된 서버 구성을 복사합니다.</span>
        </button>
      </div>`
    : `
      <p class="deal-svradd-lead">
        구성(품목·수량)만 가져옵니다. <b>판매가·소스는 승계되지 않습니다</b> —
        다른 딜의 값이라 이 거래에 그대로 쓸 수 없습니다.
      </p>
      <input class="deal-svradd-search" type="text" data-action="server-add-search"
             placeholder="거래처 · 서버 모델 · 딜명 검색" value="${escapeHtml(m.query)}" />
      <div class="deal-svradd-results">${renderServerAddResults(d)}</div>`;

  // 마크업은 딜 상세의 다른 모달(deal-rfq-modal 계열)과 같은 구조를 쓴다 —
  // 오버레이·다이얼로그·헤더/바디 CSS 가 그 이름에 걸려 있다.
  return `
    <div class="deal-rfq-modal" data-server-add-modal>
      <div class="deal-rfq-modal-overlay" data-action="server-add-close"></div>
      <div class="deal-rfq-modal-dialog deal-svradd-dialog" role="dialog" aria-label="서버 추가">
        <header class="deal-rfq-modal-header">
          <h2>서버 추가</h2>
          <button class="deal-rfq-modal-close" type="button"
                  data-action="server-add-close" aria-label="닫기">✕</button>
        </header>
        <div class="deal-rfq-modal-body">${body}</div>
      </div>
    </div>
  `;
}

/**
 * 구성 '완료' 시각 표기 — `2026/08/14 08:26`.
 *
 * 원천은 서버 헤더행의 `deal_item.created_at` 이다. 편집기는 **완료가 유일한
 * 커밋 지점**이라(ServerConfigView 파일 헤더) 완료를 누르는 그 순간에만
 * 이 행이 INSERT 된다 — 중간에 나가면 행 자체가 안 생긴다. 그래서 이 값은
 * "만들기 시작한 시각" 이 아니라 완료를 누른 시각이고, 전용 컬럼 없이
 * 오염 없는 완료 시각이 된다.
 *
 * ★ `updated_at` 이 아닌 이유(2026-08-14 확정): 딜 상세에서 서버 **대수·
 *   번들가만** 인라인 수정해도(PATCH /server/:id/header) updated_at 이 올라가
 *   순수한 완료 시각이 아니다. 반대급부로 구성을 고쳐 완료를 다시 눌러도
 *   이 날짜는 그대로다 — 최초 완료 시각으로 고정하는 쪽을 택했다.
 *
 * 로컬 시간대로 찍는다 — 목록을 보는 사람이 한국에 있다.
 */
function doneAt(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())}`
       + ` ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 검색 결과만 — 입력 중 갱신하는 리전(검색 input 의 caret 보호). */
export function renderServerAddResults(d: DealDetailData): string {
  const m = d.serverAdd;
  if (m.saving) return '<p class="text-muted">불러오는 중…</p>';
  if (m.error)  return `<p class="text-danger">${escapeHtml(m.error)}</p>`;
  // 첫 페이지 로딩 중에만 자리표시 — '더 보기' 중에는 기존 목록을 유지한다.
  if (m.searching && m.hits.length === 0) return '<p class="text-muted">검색 중…</p>';
  if (m.hits.length === 0) {
    return `<p class="text-muted">${m.query.trim()
      ? '일치하는 서버 구성이 없습니다.'
      : '저장된 서버 구성이 없습니다.'}</p>`;
  }

  const rows = m.hits.map((h) => {
    // 섀시 모델은 `chassis.model` 원본이 정본이다. 카탈로그에 없는 섀시
    // (임시등록 · pre_configured_model · 옛 데이터)면 백엔드가 null 을 주므로
    // 약식 요약의 첫 줄로 폴백한다 (2026-08-14 확정).
    const model = h.chassis_model ?? firstDescriptionLine(h.item_description);
    // 편집기 H1 과 **같은 함수**다. 각자 조립하면 목록에서 고른 이름과
    // 열어 본 이름이 갈라진다.
    const name = buildServerConfigName(h.customer_name, model, h.server_condition);
    const meta = [
      h.config_no == null ? '구성' : `구성${h.config_no}`,
      `구성품 ${h.child_count}개`,
    ].join(', ');
    return `
      <button class="deal-svradd-hit" type="button" data-action="server-add-pick"
              data-server-item-id="${escapeHtml(h.server_item_id)}">
        <span class="deal-svradd-hit-date">${escapeHtml(doneAt(h.created_at))}</span>
        <span class="deal-svradd-hit-model">${escapeHtml(name)}</span>
        <span class="deal-svradd-hit-meta">${escapeHtml(meta)}</span>
      </button>`;
  }).join('');

  return `
    ${rows}
    <div class="deal-svradd-foot">
      <span class="deal-svradd-count">${m.hits.length}개 표시</span>
      ${m.hasMore
        ? `<button class="btn btn-ghost" type="button" data-action="server-add-more"
                   ${m.searching ? 'disabled' : ''}>${
             m.searching ? '불러오는 중…' : '더 보기'}</button>`
        : ''}
    </div>
  `;
}

/** 서버 그룹 하나 — 헤더행 + (펼침 시) 자식행들. */
export function renderServerGroup(
  g:        ServerGroup,
  expanded: boolean,
  menuId:   string | null,
  locked:   boolean,
  /** 자식행 ⋯ 메뉴가 열린 deal_item id (품목 행과 같은 state 를 공유). */
  itemMenuId: string | null = null,
  /** 구성품 소스 dropdown 의 후보 (GET /server-config/sources). */
  sources: SourcePool = { loading: false, stock: [], rfq: [] },
): string {
  const s = g.server;
  const sid = escapeHtml(String(s.id));
  const condLabel = s.server_condition === 'NEW' ? '신품 서버'
    : s.server_condition === 'USED' ? '중고 서버' : '서버';
  // 접힌 상태의 품명칸 = 약식 요약.
  //   정본은 **server_summary** 다 — 백엔드가 조회할 때마다 자식행 + 카탈로그
  //   스펙으로 다시 만들어 주는 파생값이다(2026-08-21). 저장된 문자열이
  //   아니므로 수량을 고치면 다음 조회에서 바로 따라온다.
  //
  //   예전에는 item_description 을 읽었다. 거기에 요약 8줄이 통째로 저장돼
  //   있었는데, 그 값은 자식행 수량으로 계산되는 파생값인데도 무효화 규칙이
  //   없어 낡았다(딜품목 1655 — SSD 수량 5인데 요약은 *3EA).
  //
  //   폴백 두 단: 백엔드가 아직 안 올라간 경우 item_description(이제 제목
  //   한 줄)이 오고, 그마저 비면 예전처럼 자식행으로 만들어 본다.
  const summary = s.server_summary?.trim()
    || s.item_description?.trim()
    || serverSummary(g.children);
  const bundle = s.unit_price_override;

  // ⋯ 메뉴 — 확정 구성에서는 노출하지 않는다(수정·복제·삭제 모두 미확정 전용).
  //   마크업은 일반 품목 행과 같은 것을 쓴다(트리거/팝오버/오버레이 CSS 공유).
  const opt = 'width:100%;border:0;background:transparent;text-align:left;font:inherit';
  const menu = locked ? '' : `
    <button class="btn btn-ghost btn-icon deal-item-menu-trigger" type="button"
            data-action="server-menu-toggle" data-server-id="${sid}"
            title="서버 작업" aria-label="서버 작업">⋯</button>
    ${menuId === String(s.id) ? `
      <div class="deal-tag-pop-overlay" data-action="server-menu-close"></div>
      <div class="deal-tag-pop deal-item-menu">
        <button class="deal-tag-opt" type="button" style="${opt}"
                data-action="server-edit"      data-server-id="${sid}">수정</button>
        <button class="deal-tag-opt" type="button" style="${opt}"
                data-action="server-duplicate" data-server-id="${sid}">복제</button>
        <button class="deal-tag-opt text-danger" type="button" style="${opt}"
                data-action="server-delete"    data-server-id="${sid}">삭제</button>
      </div>` : ''}
  `;

  return `
    <tr class="deal-server-row${expanded ? ' is-expanded' : ''}${
      // 그룹 외곽선의 아랫변을 누가 그리는가.
      //   접힘        → 헤더행 자신
      //   펼침 + 자식 → 마지막 자식행(is-last)
      //   펼침 + 자식 없음 → 다시 헤더행 (안 그러면 프레임이 열린 채 남는다)
      expanded && g.children.length > 0 ? '' : ' is-frame-end'
    }" data-server-id="${sid}">
      <td class="deal-item-check"></td>
      <td class="deal-server-toggle-cell">
        <button class="deal-server-chevron" type="button"
                data-action="server-toggle" data-server-id="${sid}"
                aria-expanded="${expanded}" aria-label="구성품 ${expanded ? '접기' : '펼치기'}">
          ${expanded ? '▾' : '▸'}
        </button>
        <span class="deal-server-label">Server</span>
      </td>
      <td class="deal-server-summary-cell">
        <button class="deal-server-summary" type="button"
                data-action="server-toggle" data-server-id="${sid}"
                aria-expanded="${expanded}" aria-label="구성품 ${expanded ? '접기' : '펼치기'}">${
          summary
            // 표시용으로만 샤시 줄 끝에 `Server` 를 붙인다. `summary` 자체(=저장된
            // item_description)는 그대로 두고 그린 문자열만 바꾼다.
            ? escapeHtml(withServerSuffix(summary)).replace(/\n/g, '<br>')
            : '<span class="text-muted">구성품이 없습니다</span>'
        }</button>
        ${(() => {
          const bad = g.children.filter((c) => !!c.is_unregistered || !!(c as { is_pending?: boolean }).is_pending);
          return bad.length ? shorthandFixWarningHtml(bad.map(shorthandItemLabel)) : '';
        })()}
      </td>
      <td class="text-center deal-server-cond">${escapeHtml(condLabel)}</td>
      <td class="text-center deal-server-qty">${locked
        ? `${s.server_quantity ?? 1} EA`
        : `<input type="number" min="1" step="1" class="deal-server-qty-input"
                  data-action="server-qty" data-server-id="${sid}"
                  value="${s.server_quantity ?? 1}" aria-label="서버 대수" />`}</td>
      <td></td>
      <td class="text-center deal-server-bundle">${locked
        ? (bundle == null ? '<span class="text-muted">판매가 미입력</span>' : `₩${won(bundle)}`)
        : `<input type="text" inputmode="numeric" class="deal-sell-price-input"
                  data-action="server-bundle" data-server-id="${sid}"
                  value="${bundle == null ? '' : escapeHtml(won(bundle))}"
                  placeholder="서버 1대 판매가" aria-label="서버 1대 판매가" />`}</td>
      <td class="text-center deal-tag-cell">${menu}</td>
    </tr>
    ${expanded
      ? g.children.map((c, i) => renderServerChildRow(
          c, itemMenuId === String(c.id), locked, sources,
          // 마지막 자식 = 그룹 외곽선의 아랫변. 이게 없으면 펼쳤을 때 어디까지가
          // 이 서버인지 안 보여서 뒤따르는 개별 품목과 섞여 보인다.
          i === g.children.length - 1,
        )).join('')
      : ''}
  `;
}

/**
 * 서버 내부 부품행 — **소스와 참고 판매가만** 편집 가능하다.
 *
 * 무엇이 어디 소유인지는 HANDOFF 소유표가 정한다:
 *   수량·카테고리·디스크립션 → Config 원천. 여기서는 읽기 전용 projection.
 *   소스·구성품 판매가       → Deal 에서 편집 가능. RFQ 가격을 받고 소스를
 *                              고르는 일이 견적 직전에 벌어지는데, 그때마다
 *                              별탭 편집기를 열게 하면 흐름이 끊긴다.
 * 구성품 판매가는 서버 번들가와 **독립인 참고값**이라 고객 총액을 바꾸지 않는다.
 *
 * 작업칸에는 `니즈 생성`만 둔다. 구성 변경(수정/복제/삭제)은 서버 단위이지
 * 부품 단위가 아니므로 여기 두면 안 된다. 니즈는 부품 단위로 나가므로 필요하다.
 */
function renderServerChildRow(
  c: Item, menuOpen: boolean, locked: boolean, sources: SourcePool, isLast: boolean,
): string {
  const qty = c.quantity ?? 1;
  const cid = escapeHtml(String(c.id));
  // 마크업·액션은 일반 품목 행(render-items.ts)과 같은 것을 쓴다 —
  // 트리거/팝오버/오버레이 CSS 와 열림 state(itemMenuId) 를 공유해야 한다.
  const menu = locked ? '' : `
    <button class="btn btn-ghost btn-icon deal-item-menu-trigger" type="button"
            data-action="item-menu-open" data-deal-item-id="${cid}"
            title="품목 작업" aria-label="품목 작업">⋯</button>
    ${menuOpen ? `
      <div class="deal-tag-pop-overlay" data-action="item-menu-close"></div>
      <div class="deal-tag-pop deal-item-menu">
        <button class="deal-tag-opt" type="button"
                style="width:100%;border:0;background:transparent;text-align:left;font:inherit"
                data-action="item-need-create" data-deal-item-id="${cid}">니즈 생성</button>
      </div>` : ''}
  `;
  return `
    <tr class="deal-server-child${isLast ? ' is-last' : ''}" data-item-id="${escapeHtml(String(c.id))}">
      <td class="deal-item-check">
        <input type="checkbox" data-action="item-check"
               data-item-id="${escapeHtml(String(c.id))}" aria-label="니즈 생성 선택" />
      </td>
      <td class="deal-server-child-cat">
        <div class="deal-server-child-cat-label">${escapeHtml(c.spec_category ?? '-')}</div>
        ${c.option_part ? `<code class="deal-server-child-part">${escapeHtml(c.option_part)}</code>` : ''}
        <!-- 기본 구성으로 자동으로 담긴 품목(마이그 174). 서버 구성 편집기·견적 모달과
             같은 표기 — 소스가 왜 잠겨 있는지를 이 뱃지가 설명한다. -->
        ${c.is_base_part ? '<span class="deal-base-chip">기본</span>' : ''}
      </td>
      <td class="deal-server-child-desc">
      <!-- 구성품이 **개별 행**으로 나올 때는 카탈로그 원문(description_raw)을 쓴다
           (2026-08-04, 서안드레). 약식은 서버를 한 줄로 접어 보여줄 때 쓰는 표기라
           행마다 펼쳐 놓으면 오히려 정보가 줄어든다 — '1Gb 4Port' 보다
           'Broadcom BCM5719 Ethernet 1Gb 4-port BASE-T OCP3 Adapter' 가 맞다.
           원문이 없으면(미등록 파트) 약식으로 폴백한다. -->
        ${escapeHtml(c.description_raw ?? c.item_description ?? '')}
        ${c.is_unregistered ? '<span class="deal-chip-pending">임시</span>' : ''}
      </td>
      <td></td>
      <td class="text-right">${qty}EA</td>
      <td class="deal-server-child-src">${locked
        ? (c.is_base_part
            // 확정 후에도 규칙은 같다 — 기본 구성 행은 소스를 고르는 물건이 아니다.
            //   '소스 미지정' 은 "골라야 하는데 안 골랐다" 는 뜻이라 여기선 거짓이다.
            //   서버 헤더행과 같이 `-` 로 둔다.
            ? '<span class="text-muted">-</span>'
            : (c.source_label ? escapeHtml(c.source_label) : '<span class="text-muted">소스 미지정</span>'))
        : sourceSelectHtml({
            pool:     sources,
            part:     c.option_part ?? '',
            selected: selectedSourceValue(c.source_type, c.source_ref_id),
            // 기본 구성 품목은 매입 소스를 고르지 않는다(마이그 174) — 섀시 값에 포함.
            //   서버 구성 편집기·견적 모달과 같이 비활성 select 로 둔다.
            attrs:    c.is_base_part
              ? 'disabled title="기본 구성 품목은 섀시 값에 포함되어 소스를 고르지 않습니다."'
              : `data-action="server-child-source" data-deal-item-id="${cid}"`,
            cls:      'deal-server-child-srcsel',
          })}</td>
      <td class="text-right">${locked
        ? (c.sell_unit_price_krw == null ? '' : `₩${won(c.sell_unit_price_krw)}`)
        : `<input type="text" inputmode="numeric" class="deal-sell-price-input"
                  data-action="server-child-price" data-deal-item-id="${cid}"
                  value="${c.sell_unit_price_krw == null ? '' : escapeHtml(won(c.sell_unit_price_krw))}"
                  placeholder="-" aria-label="구성품 판매가" />`}</td>
      <td class="text-center deal-tag-cell">${menu}</td>
    </tr>
  `;
}
